// embedLibrary.tsx — Built-in tilt-up embed type library + EmbedPalette UI component
// Provides standard embed types for Weld Connections, Brace Points, and Pick Points.
// Project-specific custom types are appended via ProjectSettings.embedTypes.
//
// Usage:
//   import { EmbedPalette } from '../utils/embedLibrary';
//   <EmbedPalette category="weld" settings={project.settings} onSelect={entry => addSlot(entry.data)} />

import { useState } from 'react';
import type { EmbedCategory, EmbedTypeEntry, ProjectSettings, WcData, BpData, PpData } from '../types/panelvar';
export type { EmbedCategory, EmbedTypeEntry };

// ── Built-in embed type catalogue ─────────────────────────────────────────────
export const BUILTIN_EMBEDS: EmbedTypeEntry[] = [
  // ── Weld plates (most common tilt-up connection plates) ──────────────────
  {
    id: '4A', label: '4A', desc: '4″×4″ Weld Plate  ⅜″t  4-⅝″ studs',
    category: 'weld',
    data: { mark: '4A', type: 'plate', width: 4, height: 4, thick: 0.375, studs: 4, studDia: 0.625 } as Partial<WcData>,
  },
  {
    id: '5A', label: '5A', desc: '5″×5″ Weld Plate  ½″t  4-¾″ studs',
    category: 'weld',
    data: { mark: '5A', type: 'plate', width: 5, height: 5, thick: 0.5, studs: 4, studDia: 0.75 } as Partial<WcData>,
  },
  {
    id: '6A', label: '6A', desc: '6″×6″ Weld Plate  ½″t  4-¾″ studs',
    category: 'weld',
    data: { mark: '6A', type: 'plate', width: 6, height: 6, thick: 0.5, studs: 4, studDia: 0.75 } as Partial<WcData>,
  },
  {
    id: '8A', label: '8A', desc: '8″×8″ Weld Plate  ½″t  4-¾″ studs',
    category: 'weld',
    data: { mark: '8A', type: 'plate', width: 8, height: 8, thick: 0.5, studs: 4, studDia: 0.75 } as Partial<WcData>,
  },
  {
    id: 'M1', label: 'M1', desc: 'Misc Embed  6″×6″  ⅜″t',
    category: 'weld',
    data: { mark: 'M1', type: 'embed', width: 6, height: 6, thick: 0.375, studs: 0, studDia: 0 } as Partial<WcData>,
  },
  {
    id: 'M2', label: 'M2', desc: 'Misc Embed  8″×8″  ½″t',
    category: 'weld',
    data: { mark: 'M2', type: 'embed', width: 8, height: 8, thick: 0.5, studs: 0, studDia: 0 } as Partial<WcData>,
  },
  {
    id: 'ANG1', label: 'ANG1', desc: 'Angle Embed  4×4×¼″',
    category: 'weld',
    data: { mark: 'ANG1', type: 'angle', width: 4, height: 4, thick: 0.25, studs: 2, studDia: 0.625 } as Partial<WcData>,
  },
  // ── Lifting inserts ──────────────────────────────────────────────────────
  {
    id: 'R1', label: 'R1', desc: '1¼″ Lifting Insert  5T  12″ embed',
    category: 'pick',
    data: { mark: 'R1', size: 1.25, capacity: 5, embedLen: 12, load: 0 } as Partial<PpData>,
  },
  {
    id: 'R2', label: 'R2', desc: '1½″ Lifting Insert  8T  14″ embed',
    category: 'pick',
    data: { mark: 'R2', size: 1.5, capacity: 8, embedLen: 14, load: 0 } as Partial<PpData>,
  },
  {
    id: 'R3', label: 'R3', desc: '2″ Lifting Insert  12T  18″ embed',
    category: 'pick',
    data: { mark: 'R3', size: 2, capacity: 12, embedLen: 18, load: 0 } as Partial<PpData>,
  },
  // ── Brace inserts ────────────────────────────────────────────────────────
  {
    id: 'SB1', label: 'SB1', desc: '1¼″ Strongback Insert  12″ embed',
    category: 'brace',
    data: { mark: 'SB1', size: 1.25, type: 'strongback', embedLen: 12 } as Partial<BpData>,
  },
  {
    id: 'SB2', label: 'SB2', desc: '1½″ Strongback Insert  14″ embed',
    category: 'brace',
    data: { mark: 'SB2', size: 1.5, type: 'strongback', embedLen: 14 } as Partial<BpData>,
  },
  {
    id: 'SB3', label: 'SB3', desc: '2″ Strongback Insert  18″ embed',
    category: 'brace',
    data: { mark: 'SB3', size: 2, type: 'strongback', embedLen: 18 } as Partial<BpData>,
  },
];

// ── Merge built-ins with project-specific types ────────────────────────────
export function getAllEmbeds(settings?: ProjectSettings | null): EmbedTypeEntry[] {
  const custom: EmbedTypeEntry[] = settings?.embedTypes ?? [];
  const customIds = new Set(custom.map(e => e.id));
  return [
    ...BUILTIN_EMBEDS.filter(e => !customIds.has(e.id)),
    ...custom,
  ];
}

// ── EmbedPalette component ─────────────────────────────────────────────────
interface PaletteProps {
  category:  EmbedCategory;
  settings?: ProjectSettings | null;
  onSelect:  (entry: EmbedTypeEntry) => void;
}

export function EmbedPalette({ category, settings, onSelect }: PaletteProps) {
  const [open, setOpen] = useState(false);
  const embeds = getAllEmbeds(settings).filter(e => e.category === category);
  if (embeds.length === 0) return null;

  const categoryLabel =
    category === 'weld'  ? 'Weld / Embed Types' :
    category === 'brace' ? 'Brace Insert Types'  :
                           'Pick Insert Types';

  return (
    <div className="embed-palette">
      <button
        className="embed-palette-toggle"
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        ⚡ {categoryLabel} <span className="embed-palette-chevron">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="embed-palette-grid">
          {embeds.map(e => (
            <button
              key={e.id}
              type="button"
              className="embed-card"
              title={e.desc}
              onClick={() => { onSelect(e); setOpen(false); }}
            >
              <span className="embed-card-label">{e.label}</span>
              <span className="embed-card-desc">{e.desc}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
